/**
 * RequestContext - Client-side mirror of Ruby's RequestContext API
 *
 * Provides entity/platform/version detection for JS components.
 * Uses getRankersUserAgent() internally which checks URL param then global value.
 *
 * @see app/view_models/request_context.rb
 * @see plans/2026-01-24-request-context-for-js-components-via-url.md
 */

import { getRankersUserAgent } from "../constants/rankers_user_agent";
import { getRankersEntityTypeCode, getRankersEntityCode } from "../constants/rankers_entity";
import { isMobileBrowser, isIos, isAndroid } from "../helpers/device";

const CAMPING_NZ_IDENTIFIER = 'campingnz';

// The web online app is addressed as a glob client under the SAME family identifier
// ("campingnz/web/1.4.0"), so a bare identifier match would misread it as native.
// Native = identifier present AND not the web platform token; the bare identifier
// (no platform segment) stays native for the legacy fallback URLs.
// Mirrors Ruby `RequestContext#request_from_camping_nz_native_app?`.
// See plans/2026-07-16--client-neutral-ua-glob-api-addressing.md.
const WEB_CLIENT_TOKEN = `${CAMPING_NZ_IDENTIFIER}/web/`;

// The entity type_code the server injects for an online WEB app (Jucy, Camping NZ, …).
// It is the OnlineApp preconfig `type_code` (config/models/online_apps/*.yml → 'ONLINE_APP'),
// NOT the `default_app_type_code` ('WEB_APP') which is the web-vs-native variant. Mirrors
// Ruby RequestContext#entity_type_code for a web_app entity.
const ONLINE_APP_ENTITY_TYPE_CODE = 'ONLINE_APP';

function getUserAgent() {
    return getRankersUserAgent() || '';
}

export const requestContext = {
    /**
     * Check if request is from a native app.
     * @returns {boolean}
     */
    isNativeApp() {
        const userAgent = getUserAgent();
        if (!userAgent.includes(CAMPING_NZ_IDENTIFIER)) return false;
        return !userAgent.toLowerCase().includes(WEB_CLIENT_TOKEN);
    },

    /**
     * Whether this page is an online WEB app (Jucy, Camping NZ, … — entity type ONLINE_APP),
     * as opposed to the Rankers website or a native app. Uses the authoritative, cache-stable
     * server-injected entity type (host + URI based), NOT the user agent — so it is correct even
     * for shared/cross-origin-injected content (e.g. the global nav pack) rendered on the www
     * host but running in an online-app page. Mirrors Ruby RequestContext#is_web_app?.
     * @returns {boolean}
     */
    isOnlineApp() {
        return this.entityTypeCode() === ONLINE_APP_ENTITY_TYPE_CODE;
    },

    /**
     * The server-resolved entity type code for this page (e.g. "RANKERS_WEBSITE",
     * "WEB_APP", "NATIVE_APP"). Authoritative — derived server-side from host + URI
     * and injected at render time, so it survives client-side beacons to other
     * endpoints (e.g. /rum/lcp) that would otherwise lose the page's namespace.
     * @returns {string}
     */
    entityTypeCode() {
        return getRankersEntityTypeCode();
    },

    /**
     * The server-resolved entity code for this page.
     * @returns {string}
     */
    entityCode() {
        return getRankersEntityCode();
    },

    /**
     * Whether the *browser device* looks like a mobile/tablet. Mirrors the server's
     * `RequestContext::HasContainer#is_mobile_browser?` but is more accurate (resolves
     * iPadOS 13+). NOTE: this is device detection, NOT the native-app platform — see
     * `isNativeApp()`. Delegates to `helpers/device.js`.
     * @returns {boolean}
     */
    isMobileBrowser() {
        return isMobileBrowser();
    },

    /**
     * Whether the browser device looks like iOS (incl. iPadOS 13+).
     * Mirrors the server's `is_ios_browser?`.
     * @returns {boolean}
     */
    isIosBrowser() {
        return isIos();
    },

    /**
     * Whether the browser device looks like Android. Mirrors the server's `is_android_browser?`.
     * @returns {boolean}
     */
    isAndroidBrowser() {
        return isAndroid();
    },

    // isCampingNzNativeApp() {
    //     return this.isNativeApp();
    // },

    // get platform() {
    //     const ua = getUserAgent();
    //     if (ua.includes('/ios/') || ua.includes('/iOS/')) return 'ios';
    //     if (ua.includes('/android/') || ua.includes('/Android/')) return 'android';
    //     return 'web';
    // },

    // isIosPlatform() { return this.platform === 'ios'; },
    // isAndroidPlatform() { return this.platform === 'android'; },

    // get version() {
    //     const match = getUserAgent().match(/(\d+\.\d+\.\d+)/);
    //     return match ? match[1] : null;
    // }
};
